import React, { useState, useEffect, useRef } from 'react'
import { Dialog } from '@headlessui/react'
import { motion, AnimatePresence } from 'framer-motion'
import { Bars3Icon, XMarkIcon } from '@heroicons/react/24/outline'
import { 
  CalendarIcon, ChatBubbleLeftIcon, CreditCardIcon, 
  ShoppingCartIcon, UserGroupIcon, CheckCircleIcon,
  ArrowRightIcon, DevicePhoneMobileIcon,
  FilmIcon, BookOpenIcon, BriefcaseIcon,
  GlobeAltIcon, PuzzlePieceIcon, HeartIcon,
  UserCircleIcon, ChevronDownIcon
} from '@heroicons/react/24/solid'
import { useNavigate, Link } from 'react-router-dom';
import { Disclosure } from '@headlessui/react'

const navigation = []  // Empty the navigation array to remove tabs

const features = [
  {
    name: 'Smart Calendar',
    description: 'Scheduling and time management. Organize your day and share it with your friends and family.',
    icon: CalendarIcon,
    image: `${process.env.REACT_APP_CDN_URL}/illustrations/calendar.png`,
    color: 'from-pri-yellow to-pri-green'
  },
  {
    name: 'AI Meal Planner',
    description: 'Personalized meal planning with AI that adapts to your preferences, dietary needs, and schedule.',
    icon: GlobeAltIcon,
    image: `${process.env.REACT_APP_CDN_URL}/illustrations/meal-planner.png`,
    color: 'from-soft-pink to-mint-green'
  },
  {
    name: 'Social Network',
    description: 'Connect with family and friends.',
    icon: UserGroupIcon,
    image: `${process.env.REACT_APP_CDN_URL}/illustrations/connect.png`,
    color: 'from-blue-500 to-pri-green'
  },
]





const illustrations = [
  { icon: CalendarIcon, color: 'bg-yellow-400', delay: 0 },
  { icon: ChatBubbleLeftIcon, color: 'bg-green-400', delay: 0.2 },
  { icon: CreditCardIcon, color: 'bg-blue-400', delay: 0.4 },
  { icon: ShoppingCartIcon, color: 'bg-purple-400', delay: 0.6 },
  { icon: UserGroupIcon, color: 'bg-red-400', delay: 0.8 },
  { icon: FilmIcon, color: 'bg-indigo-400', delay: 1 },
  { icon: HeartIcon, color: 'bg-pink-400', delay: 1.2 },
  { icon: BookOpenIcon, color: 'bg-orange-400', delay: 1.4 },
  { icon: BriefcaseIcon, color: 'bg-teal-400', delay: 1.6 },
  { icon: GlobeAltIcon, color: 'bg-cyan-400', delay: 1.8 },
  { icon: PuzzlePieceIcon, color: 'bg-lime-400', delay: 2 },
  { icon: CheckCircleIcon, color: 'bg-rose-400', delay: 2.2 },
];

const FloatingIcon = ({ Icon, color, index, total }) => {
  // Calculate multiple orbits
  const angle = (index * (360 / total)) * (Math.PI / 180);
  const radius = 150 + (index % 2) * 50; // Creates two orbital rings
  const x = Math.cos(angle) * radius;
  const y = Math.sin(angle) * radius;

  return (
    <motion.div
      className={`absolute rounded-full p-3 ${color} shadow-lg backdrop-blur-sm
                 border border-white/20 hover:scale-110 transition-transform`}
      initial={{ x: 0, y: 0, opacity: 0 }}
      animate={{
        x: [x, x + 20, x - 20, x],
        y: [y, y - 20, y + 20, y],
        rotate: [0, 360],
        opacity: 1,
      }}
      transition={{
        duration: 20 + index * 2,
        repeat: Infinity,
        repeatType: "reverse",
        ease: "easeInOut"
      }}
      whileHover={{
        scale: 1.2,
        boxShadow: "0 0 20px rgba(255,255,255,0.3)",
      }}
    >
      <Icon className="w-6 h-6 text-white" />
    </motion.div>
  );
};

// Add this new component for the central phone animation
const CentralPhone = () => (
  <motion.div
    initial={{ scale: 0.8, opacity: 0 }}
    animate={{ scale: 1, opacity: 1 }}
    transition={{ duration: 1, delay: 0.5 }}
    className="relative z-10"
  >
    <motion.div
      className="relative"
      animate={{
        y: [-10, 10, -10],
        rotate: [-5, 5, -5],
      }}
      transition={{
        duration: 6,
        repeat: Infinity,
        ease: "easeInOut",
      }}
    >
      <motion.div
        className="absolute inset-0 bg-gradient-to-r from-yellow-400/30 to-green-400/30 rounded-3xl blur-xl"
        animate={{
          scale: [1, 1.2, 1],
          opacity: [0.5, 0.8, 0.5],
        }}
        transition={{
          duration: 4,
          repeat: Infinity,
          ease: "easeInOut",
        }}
      />
      <DevicePhoneMobileIcon className="w-32 h-32 text-white relative z-10" />
    </motion.div>
  </motion.div>
);

// Add this component for decorative rings
const DecorativeRings = () => (
  <>
    <motion.div
      className="absolute inset-0 border-4 border-white/5 rounded-full"
      animate={{ 
        rotate: 360,
        scale: [1, 1.1, 1],
      }}
      transition={{ 
        duration: 20, 
        repeat: Infinity, 
        ease: "linear",
      }}
    />
    <motion.div
      className="absolute inset-8 border-4 border-white/10 rounded-full"
      animate={{ 
        rotate: -360,
        scale: [1.1, 1, 1.1],
      }}
      transition={{ 
        duration: 25, 
        repeat: Infinity, 
        ease: "linear",
      }}
    />
    <motion.div
      className="absolute inset-16 border-4 border-gradient-to-r from-yellow-400/20 to-green-400/20 rounded-full"
      animate={{ 
        rotate: 180,
        scale: [1, 1.2, 1],
      }}
      transition={{ 
        duration: 30, 
        repeat: Infinity, 
        ease: "linear",
      }}
    />
  </>
);

const MotionBackground = () => (
  <motion.div
    className="absolute inset-0 -z-10 bg-gradient-to-br from-pri-dark via-sec-dark to-ter-dark"
    initial={{ opacity: 0.8 }}
    animate={{
      opacity: [0.8, 0.9, 0.8],
      transition: { duration: 8, repeat: Infinity, ease: "easeInOut" }
    }}
  >
    <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNTAwIiBoZWlnaHQ9IjUwMCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZmlsdGVyIGlkPSJub2lzZSI+PGZlVHVyYnVsZW5jZSB0eXBlPSJmcmFjdGFsTm9pc2UiIGJhc2VGcmVxdWVuY3k9IjAuNjUiIG51bU9jdGF2ZXM9IjMiIHN0aXRjaFRpbGVzPSJzdGl0Y2giLz48L2ZpbHRlcj48cmVjdCB3aWR0aD0iNTAwIiBoZWlnaHQ9IjUwMCIgZmlsdGVyPSJ1cmwoI25vaXNlKSIgb3BhY2l0eT0iMC4xNSIvPjwvc3ZnPg==')]" />
  </motion.div>
);

const FloatingElement = ({ children, delay = 0 }) => (
  <motion.div
    initial={{ y: 0 }}
    animate={{ y: [-10, 10, -10] }}
    transition={{
      duration: 5,
      repeat: Infinity,
      delay: delay,
      ease: "easeInOut"
    }}
  >
    {children}
  </motion.div>
);

// Add these new components for better visualization
const EcosystemVisualization = () => {
  // Add responsive radius calculation
  const getRadius = (index) => {
    // Smaller radius for mobile screens
    const baseRadius = window.innerWidth < 640 ? 100 : 150;
    const orbitOffset = window.innerWidth < 640 ? 20 : 40;
    return baseRadius + (index % 3) * orbitOffset;
  };

  return (
    <div className="relative w-full h-[300px] sm:h-[400px] flex items-center justify-center">
      {/* Central Hub - Glowing Sphere - Smaller on mobile */}
      <motion.div
        className="absolute w-16 h-16 sm:w-24 sm:h-24 bg-gradient-to-r from-yellow-400 to-green-400 rounded-full
                   filter blur-md opacity-80"
        animate={{
          scale: [1, 1.2, 1],
          opacity: [0.5, 0.8, 0.5],
        }}
        transition={{
          duration: 3,
          repeat: Infinity,
          ease: "easeInOut"
        }}
      />

      {/* Orbiting Paths - Smaller on mobile */}
      {[...Array(3)].map((_, index) => (
        <motion.div
          key={`orbit-${index}`}
          className="absolute border border-white/10 rounded-full"
          style={{
            width: `${200 + index * (window.innerWidth < 640 ? 40 : 80)}px`,
            height: `${200 + index * (window.innerWidth < 640 ? 40 : 80)}px`,
          }}
          animate={{
            rotate: 360,
            scale: [1, 1.05, 1],
          }}
          transition={{
            duration: 20 + index * 5,
            repeat: Infinity,
            ease: "linear",
          }}
        />
      ))}

      {/* Floating Elements - Adjusted for mobile */}
      {illustrations.map((item, index) => {
        const angle = (index * (360 / illustrations.length)) * (Math.PI / 180);
        const radius = getRadius(index);
        const x = Math.cos(angle) * radius;
        const y = Math.sin(angle) * radius;

        return (
          <motion.div
            key={index}
            className="absolute"
            style={{ x, y }}
            animate={{
              x: [x, x + (window.innerWidth < 640 ? 10 : 15), x - (window.innerWidth < 640 ? 10 : 15), x],
              y: [y, y - (window.innerWidth < 640 ? 10 : 15), y + (window.innerWidth < 640 ? 10 : 15), y],
              rotateZ: [0, 360],
              scale: [1, 1.1, 1],
            }}
            transition={{
              duration: 10 + index * 2,
              repeat: Infinity,
              repeatType: "reverse",
              ease: "easeInOut",
            }}
          >
            {/* Feature Card - Smaller on mobile */}
            <motion.div
              whileHover={{ scale: 1.1, rotate: -5 }}
              className={`w-10 h-10 sm:w-14 sm:h-14 ${item.color} rounded-xl sm:rounded-2xl p-2 sm:p-3
                         backdrop-blur-lg shadow-lg border border-white/20
                         flex items-center justify-center transform
                         hover:shadow-2xl transition-all duration-300`}
            >
              <item.icon className="w-5 h-5 sm:w-8 sm:h-8 text-white" />
            </motion.div>

            {/* Connection Lines - Hidden on mobile */}
            <motion.div
              className="absolute inset-0 pointer-events-none hidden sm:block"
              animate={{
                opacity: [0.2, 0.4, 0.2],
              }}
              transition={{
                duration: 2,
                repeat: Infinity,
                ease: "easeInOut",
              }}
            >
              <div className="w-full h-full border-l-2 border-dashed border-white/10" />
            </motion.div>
          </motion.div>
        );
      })}

      {/* Central Platform Visualization - Smaller on mobile */}
      <motion.div
        className="relative z-10 w-24 h-24 sm:w-32 sm:h-32 flex items-center justify-center"
        animate={{
          rotate: [0, 5, -5, 0],
          y: [-5, 5, -5],
        }}
        transition={{
          duration: 6,
          repeat: Infinity,
          ease: "easeInOut",
        }}
      >
        {/* Colorful Gradient Background */}
        <motion.div
          className="absolute inset-0 rounded-3xl bg-gradient-to-br from-yellow-400 via-green-400 to-blue-400 opacity-20 blur-xl"
          animate={{
            scale: [1, 1.2, 1],
            rotate: [0, 180, 0],
          }}
          transition={{
            duration: 8,
            repeat: Infinity,
            ease: "linear",
          }}
        />

        {/* Mobile Phone Illustration */}
        <motion.div
          className="relative w-24 h-28 rounded-2xl bg-gradient-to-br from-yellow-400 to-green-400 p-1"
          whileHover={{ scale: 1.05 }}
        >
          {/* Phone Frame */}
          <div className="w-full h-full bg-[#1a1f2e] rounded-[18px] p-2 relative overflow-hidden">
            {/* Screen Content */}
            <div className="w-full h-full rounded-[16px] bg-gradient-to-br from-yellow-400/10 to-green-400/10 relative">
              {/* Notch */}
              <div className="absolute top-1 left-1/2 transform -translate-x-1/2 w-8 h-1.5 bg-[#1a1f2e] rounded-full" />
              
              {/* App Icons Grid */}
              <div className="grid grid-cols-3 gap-1 p-2 mt-3">
                {[...Array(6)].map((_, i) => (
                  <motion.div
                    key={i}
                    className={`w-4 h-4 rounded-md ${
                      ['bg-yellow-400', 'bg-green-400', 'bg-blue-400', 'bg-purple-400', 'bg-pink-400', 'bg-orange-400'][i]
                    }`}
                    animate={{
                      scale: [1, 1.1, 1],
                    }}
                    transition={{
                      duration: 2,
                      delay: i * 0.2,
                      repeat: Infinity,
                      ease: "easeInOut",
                    }}
                  />
                ))}
              </div>

              {/* Animated Wave */}
              <motion.div
                className="absolute bottom-0 left-0 right-0 h-10 bg-gradient-to-t from-yellow-400/20 to-green-400/20"
                animate={{
                  y: [0, -5, 0],
                  opacity: [0.3, 0.5, 0.3],
                }}
                transition={{
                  duration: 3,
                  repeat: Infinity,
                  ease: "easeInOut",
                }}
              />
            </div>

            {/* Floating Elements */}
            {[...Array(3)].map((_, i) => (
              <motion.div
                key={i}
                className={`absolute w-2 h-2 rounded-full ${
                  ['bg-yellow-400', 'bg-green-400', 'bg-blue-400'][i]
                }`}
                style={{
                  top: `${20 + i * 30}%`,
                  right: '-6px',
                }}
                animate={{
                  x: [-10, 0, -10],
                  opacity: [0.5, 1, 0.5],
                }}
                transition={{
                  duration: 2,
                  delay: i * 0.3,
                  repeat: Infinity,
                  ease: "easeInOut",
                }}
              />
            ))}
          </div>
        </motion.div>
      </motion.div>

      {/* Particle Effects - Adjusted for mobile */}
      <motion.div
        className="absolute inset-0 pointer-events-none"
        animate={{
          background: [
            "radial-gradient(circle at 20% 30%, rgba(255, 215, 0, 0.1) 0%, transparent 50%)",
            "radial-gradient(circle at 80% 70%, rgba(152, 251, 152, 0.1) 0%, transparent 50%)",
            "radial-gradient(circle at 20% 30%, rgba(255, 215, 0, 0.1) 0%, transparent 50%)",
          ],
        }}
        transition={{
          duration: 15,
          repeat: Infinity,
          ease: "easeInOut",
        }}
      />
    </div>
  );
};

export default function LandingPage() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false)
  const [isVisible, setIsVisible] = useState({
    hero: true,
    features: false,
    ecosystem: false,
    testimonialsRight: false,
    testimonialsLeft: false,
    pricing: false,
    brands: false,
    faq: false
  });
  
  const heroRef = useRef(null);
  const featureRef = useRef(null);
  const ecosystemRef = useRef(null);
  const testimonialsRef = useRef(null);
  const statsRef = useRef(null);
  const brandsRef = useRef(null);
  const faqRef = useRef(null);
  
  const navigate = useNavigate();

  // Add navigation handler
  const handleNavigation = (path) => {
    setMobileMenuOpen(false); // Close mobile menu if open
    navigate(path);
  };

  // Add smooth scroll handler for anchor links
  const handleSmoothScroll = (id) => {
    const element = document.getElementById(id);
    element?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    const handleScroll = () => {
      if (heroRef?.current) {
        const heroRect = heroRef.current.getBoundingClientRect();
        setIsVisible(prev => ({ ...prev, hero: heroRect.top < window.innerHeight && heroRect.bottom > 0 }));
      }

      if (featureRef?.current) {
        const featureRect = featureRef.current.getBoundingClientRect();
        setIsVisible(prev => ({ ...prev, features: featureRect.top < window.innerHeight && featureRect.bottom > 0 }));
      }
      
      if (faqRef?.current) {
        const faqRect = faqRef.current.getBoundingClientRect();
        setIsVisible(prev => ({ ...prev, faq: faqRect.top < window.innerHeight && faqRect.bottom > 0 }));
      }
      
      if (ecosystemRef?.current) {
        const ecosystemRect = ecosystemRef.current.getBoundingClientRect();
        setIsVisible(prev => ({ ...prev, ecosystem: ecosystemRect.top < window.innerHeight && ecosystemRect.bottom > 0 }));
      }

      const sections = ['features', 'faq']  // Include 'faq' in the sections array
      sections.forEach(section => {
        const element = document.getElementById(section)
        if (element) {
          const rect = element.getBoundingClientRect()
          setIsVisible(prev => ({
            ...prev,
            [section]: rect.top < window.innerHeight * 0.75
          }))
        }
      })
    }

    window.addEventListener('scroll', handleScroll)
    handleScroll()
    return () => window.removeEventListener('scroll', handleScroll)
  }, [])

  return (
    <div className="relative min-h-screen overflow-hidden text-pri-white bg-pri-dark">
      <MotionBackground />
      
      {/* Header */}
      <header className="fixed inset-x-0 top-0 z-50 bg-opacity-90 bg-pri-dark backdrop-blur-md">
        <nav className="flex items-center justify-between p-2 lg:px-8" aria-label="Global">
          <motion.div 
            className="flex lg:flex-1"
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.5 }}
          >
            <a href="#" className="-m-1.5 p-1.5">
              <span className="sr-only">Jingleez Ecosystem</span>
              <img
                className="h-12 w-auto"
                src={`${process.env.REACT_APP_CDN_URL}/logo/logo-no-bg.png`}
                alt="Jingleez"
              />
            </a>
          </motion.div>
          
          <div className="flex items-center justify-end lg:flex-1">
            {/* Desktop navigation links */}
            <div className="hidden lg:flex lg:gap-x-6 lg:mr-4">
              <Link
                to="/about"
                className="text-sm font-semibold leading-6 text-mint-green hover:opacity-80 transition-colors"
              >
                About Us
              </Link>
              <Link
                to="/contact"
                className="text-sm font-semibold leading-6 text-mint-green hover:opacity-80 transition-colors"
              >
                Contact Us
              </Link>
            </div>
            
            {/* Mobile hamburger menu button */}
            <div className="flex lg:hidden">
              <motion.button
                type="button"
                className="inline-flex items-center justify-center rounded-md p-2.5 text-white"
                onClick={() => setMobileMenuOpen(true)}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                <span className="sr-only">Open main menu</span>
                <Bars3Icon className="h-6 w-6" aria-hidden="true" />
              </motion.button>
            </div>
            
            {/* Desktop login button */}
            <div className="hidden lg:flex">
              <motion.a
                href="/login"
                className="inline-flex items-center text-sm font-semibold px-5 py-2 bg-mint-green text-pri-dark rounded-full hover:opacity-90 transition-all duration-300"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                Log in <ArrowRightIcon className="w-4 h-4 ml-2" />
              </motion.a>
            </div>
          </div>
        </nav>
        <Dialog as="div" className="lg:hidden" open={mobileMenuOpen} onClose={setMobileMenuOpen}>
          <div className="fixed inset-0 z-50" />
          <Dialog.Panel className="fixed inset-y-0 right-0 z-50 w-full overflow-y-auto bg-pri-dark px-6 py-6 sm:max-w-sm sm:ring-1 sm:ring-gray-900/10">
            <div className="flex items-center justify-between">
              <a href="#" className="-m-1.5 p-1.5">
                <span className="sr-only">Jingleez</span>
                <img
                  className="h-10 w-auto"
                  src={`${process.env.REACT_APP_CDN_URL}/logo/logo-no-bg.png`}
                  alt="Jingleez"
                />
              </a>
              <button
                type="button"
                className="rounded-md p-2.5 text-white"
                onClick={() => setMobileMenuOpen(false)}
              >
                <span className="sr-only">Close menu</span>
                <XMarkIcon className="h-6 w-6" aria-hidden="true" />
              </button>
            </div>
            <div className="mt-6 flow-root">
              <div className="-my-6 divide-y divide-gray-500/10">
                <div className="space-y-2 py-6">
                  <Link
                    to="/about"
                    className="-mx-3 block rounded-lg px-3 py-2 text-base font-semibold leading-7 text-mint-green hover:bg-white/10"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    About Us
                  </Link>
                  <Link
                    to="/contact"
                    className="-mx-3 block rounded-lg px-3 py-2 text-base font-semibold leading-7 text-mint-green hover:bg-white/10"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    Contact Us
                  </Link>
                </div>
                <div className="py-6">
                  <Link
                    to="/login"
                    className="-mx-3 block rounded-lg px-3 py-2.5 text-base font-semibold leading-7 text-mint-green hover:bg-white/10"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    Log in
                  </Link>
                  <Link
                    to="/signup"
                    className="mt-4 flex w-full items-center justify-center rounded-full bg-mint-green px-3 py-2.5 text-base font-semibold text-pri-dark shadow-sm hover:opacity-90"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    Sign up
                  </Link>
                </div>
              </div>
            </div>
          </Dialog.Panel>
        </Dialog>
      </header>

      <main className="isolate">
        {/* Hero section */}
        <div className="relative pt-14">
          <div className="py-16 sm:py-24">
            <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
              {/* Two-column layout without borders */}
              <div className="flex flex-col lg:flex-row items-center lg:items-start justify-between gap-8 lg:gap-16">
                {/* Left column - Text content */}
                <motion.div
                  className="w-full lg:w-1/2 order-1 lg:order-1"
                  initial={{ opacity: 0, x: -30 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.7 }}
                >
                  {/* Hero content with left alignment and no border */}
                  <div className="relative z-10 p-6 lg:p-0">
                    <div className="relative text-left">
                      <div className="flex items-center gap-3 mb-4">
                        <h1 className="text-soft-pink text-5xl xs:text-6xl sm:text-7xl font-bold leading-tight">
                          Jingleez
                        </h1>
                        
                        {/* Now in Beta badge - moved to same row as title */}
                        <div className="inline-flex items-center px-3 py-1 rounded-full bg-white/10 backdrop-blur-sm">
                          <span className="relative flex h-2 w-2 mr-2">
                            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-mint-green opacity-75"></span>
                            <span className="relative inline-flex rounded-full h-2 w-2 bg-mint-green"></span>
                          </span>
                          <span className="text-white text-xs font-medium">Now in Beta</span>
                        </div>
                      </div>
                      
                      <div className="h-0.5 bg-gradient-to-r from-soft-pink via-mint-green to-transparent w-20 my-4" />
                      
                      <p className="text-pri-white text-xl sm:text-2xl font-medium mt-2 mb-3">
                        Building a <span className="text-soft-pink">digital ecosystem</span> to manage your life easier
                      </p>
                      
                      <p className="text-pri-gray/90 text-base sm:text-lg mb-8 max-w-md">
                      Experience a seamless and unified digital ecosystem that adapts to your lifestyle.
                      </p>

                      {/* Button layout with improved spacing and visual hierarchy - now in row for all screen sizes */}
                      <div className="flex flex-row gap-4 w-full max-w-md">
                        <motion.button
                          onClick={() => handleNavigation('/signup')}
                          className="flex-1 px-4 py-3 bg-mint-green 
                                   text-pri-dark font-bold rounded-full hover:opacity-90
                                   transition-all duration-300 flex items-center justify-center text-sm
                                   shadow-lg"
                          whileHover={{ scale: 1.03 }}
                          whileTap={{ scale: 0.97 }}
                        >
                          Get Started
                          <ArrowRightIcon className="w-4 h-4 ml-2" />
                        </motion.button>
                        <motion.button
                          onClick={() => {
                            const element = document.getElementById('features');
                            element?.scrollIntoView({ behavior: 'smooth' });
                          }}
                          className="flex-1 px-4 py-3 bg-white/5 
                                   text-white font-bold rounded-full hover:bg-white/10
                                   transition-all duration-300 flex items-center justify-center text-sm"
                          whileHover={{ scale: 1.03 }}
                          whileTap={{ scale: 0.97 }}
                        >
                          Learn More
                        </motion.button>
                      </div>
                    </div>
                  </div>
                </motion.div>
                
                {/* Right column - Original Ecosystem Visualization */}
                <motion.div
                  className="w-full lg:w-1/2 order-2 lg:order-2"
                  initial={{ opacity: 0, x: 30 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.7 }}
                >
                  <EcosystemVisualization />
                </motion.div>
              </div>
            </div>
          </div>
        </div>

        {/* Features Section */}
        <section id="features" className="py-16 sm:py-24 relative">
          <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-white/10 to-transparent"></div>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: isVisible.features ? 1 : 0 }}
            transition={{ duration: 0.5 }}
            className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8"
          >
            <div className="text-center mb-16">
              <motion.div
                className="inline-block px-4 py-1 mb-3 rounded-full bg-soft-pink/10 backdrop-blur-sm"
                initial={{ y: 20 }}
                animate={{ y: isVisible.features ? 0 : 20 }}
                transition={{ duration: 0.5 }}
              >
                <span className="text-soft-pink text-sm font-medium tracking-wider uppercase">Explore What We Offer</span>
              </motion.div>
              
              <motion.h2 
                className="text-4xl md:text-5xl font-bold mb-4"
                initial={{ y: 20 }}
                animate={{ y: isVisible.features ? 0 : 20 }}
                transition={{ duration: 0.5 }}
              >
                <span className="text-soft-pink drop-shadow-sm">
                  Powerful Features
                </span>
              </motion.h2>
              
              <motion.p 
                className="text-pri-gray text-lg max-w-2xl mx-auto"
                initial={{ y: 20 }}
                animate={{ y: isVisible.features ? 0 : 20 }}
                transition={{ duration: 0.5, delay: 0.2 }}
              >
                Everything you need to manage your life in one place
              </motion.p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 sm:gap-8 md:gap-10">
              {features.map((feature, index) => (
                <motion.div
                  key={feature.name}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ 
                    opacity: isVisible.features ? 1 : 0, 
                    y: isVisible.features ? 0 : 20 
                  }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  className="group relative overflow-hidden bg-gradient-to-br from-pri-dark/80 to-sec-dark/90 rounded-2xl 
                            shadow-lg border border-white/10 hover:border-white/20 transition-all duration-300"
                >
                  {/* Feature tile with adaptive layout - horizontal on mobile, vertical on larger screens */}
                  <div className="flex flex-col md:flex-col">
                    {/* Feature image with overlay - taller height (75%) */}
                    <div className="h-48 md:h-72 relative overflow-hidden">
                      <img 
                        src={feature.image} 
                        alt={feature.name}
                        className="w-full h-full object-cover transform group-hover:scale-105 transition-transform duration-500"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-pri-dark via-pri-dark/80 to-transparent opacity-90"></div>
                    </div>
                    
                    {/* Content - reduced height (25%) */}
                    <div className="relative z-10 p-4">
                      <h3 className="text-lg md:text-xl font-semibold mb-1 text-soft-pink group-hover:text-soft-pink transition-colors">
                        {feature.name}
                      </h3>
                      <div className="h-0.5 w-12 bg-mint-green mb-2 transform origin-left group-hover:w-20 transition-all duration-300"></div>
                      <p className="text-pri-gray text-sm md:text-base line-clamp-2">
                        {feature.description}
                      </p>
                      
                      {/* Learn more link - always visible and clickable */}
                      <Link 
                        to={`/feature/${feature.name.toLowerCase().replace(/\s+/g, '-')}`}
                        className="flex items-center gap-2 mt-2 text-mint-green font-medium hover:text-mint-green/80 transition-colors"
                      >
                        <span className="text-sm">Learn more</span>
                        <ArrowRightIcon className="w-3 h-3" />
                      </Link>
                    </div>
                  </div>
                </motion.div>
              ))}
              
              {/* Coming Soon Tile - updated to match other tiles */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ 
                  opacity: isVisible.features ? 1 : 0, 
                  y: isVisible.features ? 0 : 20 
                }}
                transition={{ duration: 0.5, delay: 0.2 }}
                className="group relative overflow-hidden bg-gradient-to-br from-pri-dark/80 to-sec-dark/90 rounded-2xl 
                          shadow-lg border border-white/10 hover:border-white/20 transition-all duration-300"
              >
                {/* Feature tile with adaptive layout - horizontal on mobile, vertical on larger screens */}
                <div className="flex flex-col md:flex-col">
                  {/* Feature image with overlay - taller height (75%) */}
                  <div className="h-48 md:h-72 relative overflow-hidden">
                    <img 
                      src={`${process.env.REACT_APP_CDN_URL}/illustrations/coming-soon.png`}
                      alt="Coming Soon"
                      className="w-full h-full object-cover transform group-hover:scale-105 transition-transform duration-500"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-pri-dark via-pri-dark/80 to-transparent opacity-90"></div>
                  </div>
                  
                  {/* Content - reduced height (25%) */}
                  <div className="relative z-10 p-4">
                    <h3 className="text-lg md:text-xl font-semibold mb-1 text-soft-pink group-hover:text-soft-pink transition-colors">
                      Coming Soon
                    </h3>
                    <div className="h-0.5 w-12 bg-mint-green mb-2 transform origin-left group-hover:w-20 transition-all duration-300"></div>
                    <p className="text-pri-gray text-sm md:text-base line-clamp-2">
                      We are building more features
                    </p>
                    
                    {/* Progress bar */}
                    <div className="w-full h-1.5 rounded-full bg-pri-dark/50 overflow-hidden mt-2">
                      <motion.div
                        className="h-full bg-gradient-to-r from-soft-pink to-mint-green rounded-full"
                        animate={{
                          width: ["30%", "70%", "30%"],
                        }}
                        transition={{
                          duration: 3,
                          repeat: Infinity,
                          ease: "easeInOut",
                        }}
                      />
                    </div>
                    
                    {/* Learn more link - always visible */}
                    <Link 
                      to="/feature/coming-soon"
                      className="flex items-center gap-2 mt-2 text-mint-green font-medium hover:text-mint-green/80 transition-colors"
                    >
                      <span className="text-sm">Learn more</span>
                      <ArrowRightIcon className="w-3 h-3" />
                    </Link>
                  </div>
                </div>
              </motion.div>
            </div>
          </motion.div>
        </section>

        {/* FAQ Section */}
        <section ref={faqRef} id="faq" className="py-24 relative">
          <motion.div
            className="max-w-5xl mx-auto px-6 lg:px-8"
            initial={{ opacity: 0 }}
            animate={{ opacity: isVisible.faq ? 1 : 0 }}
            transition={{ duration: 0.5 }}
          >
            <div className="text-center mb-12 md:mb-16">
              <motion.h2 
                className="text-4xl md:text-5xl font-bold mb-4"
                initial={{ y: 20 }}
                animate={{ y: isVisible.faq ? 0 : 20 }}
                transition={{ duration: 0.5 }}
              >
                <span className="text-mint-green drop-shadow-sm">
                  Frequently Asked Questions
                </span>
              </motion.h2>
              
              <motion.p 
                className="text-pri-gray text-lg max-w-2xl mx-auto"
                initial={{ y: 20 }}
                animate={{ y: isVisible.faq ? 0 : 20 }}
                transition={{ duration: 0.5, delay: 0.2 }}
              >
                Get answers to common questions about Jingleez
              </motion.p>
            </div>

            <div className="grid grid-cols-1 gap-4 sm:gap-6">
              {[
                // {
                //   question: "What is JINGLEEZ platform?",
                //   answer: "Jingleez is a digital ecosystem that seamlessly integrates your daily tasks, social connections, and digital experiences in one powerful platform that adapts to your lifestyle. It combines smart calendar features with social networking capabilities, allowing you to organize and share your life efficiently."
                // },
                {
                  question: "Which locations are supported?",
                  answer: "Currently, Jingleez is only available in the UK. We're working to expand our service to more regions in the future."
                },
                {
                  question: "Is there any fee for this platform?",
                  answer: "The current beta platform is completely free to use. We're focused on building a great user experience and gathering feedback before considering any premium features in the future."
                },
                {
                  question: "Why is it in beta?",
                  answer: "As part of beta, we have early access for friends and family. This allows us to gather valuable feedback and improve the platform before our full public launch."
                }
              ].map((faq, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ 
                    opacity: isVisible.faq ? 1 : 0, 
                    y: isVisible.faq ? 0 : 20 
                  }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  className="group relative overflow-hidden bg-gradient-to-br from-pri-dark/80 to-sec-dark/90 rounded-xl 
                            shadow-lg border border-white/10 hover:border-white/20 transition-all duration-300"
                >
                  <div className="p-6">
                    <Disclosure>
                      {({ open }) => (
                        <>
                          <Disclosure.Button className="flex justify-between w-full text-left">
                            <h3 className="text-xl font-semibold text-soft-pink group-hover:text-soft-pink transition-colors">
                              {faq.question}
                            </h3>
                            <ChevronDownIcon
                              className={`${
                                open ? 'transform rotate-180' : ''
                              } w-5 h-5 text-mint-green transition-transform duration-300`}
                            />
                          </Disclosure.Button>
                          <Disclosure.Panel className="mt-4">
                            <div className="h-0.5 w-16 bg-mint-green mb-4 transform origin-left transition-all duration-300"></div>
                            <p className="text-pri-gray text-base">{faq.answer}</p>
                          </Disclosure.Panel>
                        </>
                      )}
                    </Disclosure>
                  </div>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </section>

        {/* CTA Section */}
        <section className="py-24 relative overflow-hidden">
          <motion.div
            className="max-w-7xl mx-auto px-6 lg:px-8 relative z-10"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <div className="bg-gradient-to-br from-pri-dark to-sec-dark rounded-3xl p-8 md:p-16 text-center relative overflow-hidden">
              <motion.div
                className="absolute inset-0 bg-gradient-to-r from-pri-yellow/20 to-pri-green/20"
                animate={{
                  scale: [1, 1.2, 1],
                  rotate: [0, 180, 0],
                }}
                transition={{
                  duration: 20,
                  repeat: Infinity,
                  ease: "linear"
                }}
              />
              <div className="relative z-10">
                <h2 className="text-3xl md:text-4xl font-bold mb-6">
                  Ready to Get Started?
                </h2>
                <p className="text-lg text-pri-gray mb-8 max-w-2xl mx-auto">
                  Join thousands of users who are already experiencing the power of Jingleez
                </p>
                <div className="flex justify-center">
                  <motion.button
                    onClick={() => handleNavigation('/signup')}
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    className="bg-mint-green text-pri-dark px-8 py-3 rounded-full font-semibold flex items-center justify-center"
                  >
                    Sign Up Now
                    <ArrowRightIcon className="w-5 h-5 ml-2" />
                  </motion.button>
                </div>
              </div>
            </div>
          </motion.div>
        </section>
      </main>

      {/* Footer */}
      <footer className="bg-pri-dark py-12">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            <div>
              <h3 className="text-white font-bold text-lg mb-4">Jingleez</h3>
              <p className="text-pri-gray text-sm">Your digital ecosystem.</p>
            </div>
            <div>
              <h3 className="text-white font-bold text-lg mb-4">Company</h3>
              <ul className="space-y-2">
                <li>
                  <Link to="/about" className="text-pri-gray hover:text-mint-green transition-colors">
                    About Us
                  </Link>
                </li>
                <li>
                  <Link to="/contact" className="text-pri-gray hover:text-mint-green transition-colors">
                    Contact Us
                  </Link>
                </li>
              </ul>
            </div>
            <div>
              <h3 className="text-white font-bold text-lg mb-4">Resources</h3>
              <ul className="space-y-2">
                <li>
                  <a 
                    href="#features" 
                    onClick={(e) => {
                      e.preventDefault();
                      handleSmoothScroll('features');
                    }}
                    className="text-pri-gray hover:text-mint-green transition-colors cursor-pointer"
                  >
                    Features
                  </a>
                </li>
                <li>
                  <a 
                    href="#faq" 
                    onClick={(e) => {
                      e.preventDefault();
                      handleSmoothScroll('faq');
                    }}
                    className="text-pri-gray hover:text-mint-green transition-colors cursor-pointer"
                  >
                    FAQs
                  </a>
                </li>
              </ul>
            </div>
            <div>
              <h3 className="text-white font-bold text-lg mb-4">Legal</h3>
              <ul className="space-y-2">
                <li>
                  <Link to="/privacy" className="text-pri-gray hover:text-mint-green transition-colors">
                    Privacy Policy
                  </Link>
                </li>
                <li>
                  <Link to="/terms" className="text-pri-gray hover:text-mint-green transition-colors">
                    Terms of Service
                  </Link>
                </li>
              </ul>
            </div>
          </div>
          <div className="mt-8 pt-8 border-t border-pri-gray/20 text-center">
            <p className="text-pri-gray">© {new Date().getFullYear()} Jingleez. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  )
}
